import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { type ApiError } from '@/shared/api/client';
import {
  brokerQuotes,
  policyholderFile,
  policyholders,
  type BrokerQuoteInsuredOption,
  type BrokerQuoteOptions,
  type OperationDetail,
  type TermsCurrentVersion,
} from '@/shared/api/resources';
import { formatCpfCnpj, formatMoney, rateInputError } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { RichTextView } from '@/shared/ui/RichTextEditor';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';

import { CoveragesSection } from '../CoveragesSection';
import { fieldErrorOf } from '../../shared/fieldError';

import { enderecoDoTomador } from './enderecoDoTomador';
import { avisosDoPrazo, erroDaImportancia, type RascunhoDaCotacao } from './rascunho';

/** O que todo passo do rascunho recebe. */
export interface PassoDoRascunhoProps {
  rascunho: RascunhoDaCotacao;
  alterar: (parcial: Partial<RascunhoDaCotacao>) => void;
  apiError: ApiError | null;
}

const caixa =
  'mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25';

/**
 * Passo "Dados do tomador" (NCT-084 a NCT-095): o tomador da corretora, a filial e o
 * limite do cadastro, e a declaracao do termo do tipo cotacao (P-USU-2), que o envio
 * para analise exige quando a seguradora publicou o texto.
 */
export function TomadorStep({
  rascunho,
  alterar,
  apiError,
  termo,
}: PassoDoRascunhoProps & { termo: TermsCurrentVersion | null | undefined }) {
  const [busca, setBusca] = useState('');
  const tomadores = useQuery({
    queryKey: ['policyholders', 'assistente', busca],
    // So pessoa juridica: o tomador pessoa fisica e o da cotacao de pessoa fisica (a do
    // leiloeiro, P-NCT-3), e o servidor recusa o CPF nesta cotacao.
    queryFn: ({ signal }) =>
      policyholders.list({ q: busca, limit: 20, document_kind: 'cnpj' }, signal),
  });
  const filiais = useQuery({
    queryKey: ['policyholders', rascunho.policyholder_id, 'branches'],
    queryFn: ({ signal }) =>
      policyholderFile.branches(rascunho.policyholder_id, signal),
    enabled: rascunho.policyholder_id !== '',
  });

  const itens = tomadores.data?.items ?? [];
  const escolhido = itens.find((item) => item.id === rascunho.policyholder_id);
  const opcoes = itens.map((item) => ({
    value: item.id,
    label: `${item.legal_name} (${formatCpfCnpj(item.document)})`,
  }));
  if (rascunho.policyholder_id && !escolhido) {
    opcoes.unshift({
      value: rascunho.policyholder_id,
      label: rascunho.policyholder_label,
    });
  }

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader
          title="Dados do tomador"
          description="O tomador e da sua corretora. A filial sai na cotacao quando escolhida."
        />
        <CardBody className="flex flex-col gap-4">
          {tomadores.isError ? (
            <Alert tone="danger" title="Nao foi possivel carregar os tomadores">
              {tomadores.error.message}
            </Alert>
          ) : null}
          <Field
            label="Buscar tomador"
            value={busca}
            placeholder="Razao social, nome fantasia ou CNPJ"
            onChange={(event) => setBusca(event.target.value)}
          />
          {/* A busca sem resultado tem a mensagem do OnPoint (NCT-085). */}
          {tomadores.isSuccess && busca.trim() !== '' && itens.length === 0 ? (
            <p className="text-sm text-ink-600">
              Nao encontramos o CNPJ / Razao social / Nome buscado.
            </p>
          ) : null}
          <Select
            label="Tomador"
            required
            placeholder="Selecione o tomador"
            value={rascunho.policyholder_id}
            options={opcoes}
            error={fieldErrorOf(apiError, 'policyholder_id')}
            onChange={(event) => {
              const item = itens.find((t) => t.id === event.target.value);
              alterar({
                policyholder_id: event.target.value,
                policyholder_label: item
                  ? `${item.legal_name} (${formatCpfCnpj(item.document)})`
                  : '',
                policyholder_branch_id: '',
              });
            }}
          />
          {escolhido ? (
            <p className="text-xs text-ink-600">
              Limite de credito do cadastro: {formatMoney(escolhido.credit_limit)}. O
              limite disponivel e conferido pela seguradora na analise de credito.
            </p>
          ) : null}
          {escolhido ? (
            <p className="text-xs text-ink-600">
              Endereco do tomador: {enderecoDoTomador(escolhido.address)}
            </p>
          ) : null}
          {rascunho.policyholder_id ? (
            <div className="flex flex-col gap-1.5">
              <Select
                label="Filial do tomador"
                value={rascunho.policyholder_branch_id}
                options={[
                  { value: '', label: 'Sem filial (matriz)' },
                  ...(filiais.data ?? []).map((filial) => ({
                    value: filial.id,
                    label: `${filial.legal_name} (${formatCpfCnpj(filial.document)})`,
                  })),
                ]}
                error={fieldErrorOf(apiError, 'policyholder_branch_id')}
                onChange={(event) =>
                  alterar({ policyholder_branch_id: event.target.value })
                }
              />
              <Link
                to={`/tomadores/${rascunho.policyholder_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                Cadastrar filial na ficha do tomador
              </Link>
            </div>
          ) : null}
        </CardBody>
      </Card>

      {termo ? (
        <Card>
          <CardHeader title={termo.title} description="Declaracao da cotacao" />
          <CardBody className="flex flex-col gap-3">
            <div className="max-h-60 overflow-y-auto rounded-md border border-line p-3">
              <RichTextView html={termo.body} />
            </div>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={rascunho.declarou}
                onChange={(event) => alterar({ declarou: event.target.checked })}
                className={caixa}
              />
              Declaro que li e estou ciente das condicoes acima.
            </label>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Passo "Dados do segurado" (NCT-096 a NCT-103): a busca do segurado pelo nome ou pelo
 * documento, que devolve so o que a escolha precisa, e o endereco.
 */
export function SeguradoStep({ rascunho, alterar, apiError }: PassoDoRascunhoProps) {
  const [busca, setBusca] = useState('');
  const [escolhido, setEscolhido] = useState<BrokerQuoteInsuredOption | null>(null);
  const termo = busca.trim();
  const opcoes = useQuery({
    queryKey: ['insured-options', termo],
    queryFn: ({ signal }) => brokerQuotes.insuredOptions(termo, signal),
    enabled: termo.length >= 3,
  });

  const enderecos = escolhido
    ? escolhido.addresses.map((endereco) => ({
        value: endereco.id,
        label: endereco.is_main ? `${endereco.label} (principal)` : endereco.label,
      }))
    : rascunho.insured_address_id
      ? [{ value: rascunho.insured_address_id, label: rascunho.insured_address_label }]
      : [];

  return (
    <Card>
      <CardHeader
        title="Dados do segurado"
        description="Quem a garantia protege. Procure pelo nome ou pelo CPF ou CNPJ."
      />
      <CardBody className="flex flex-col gap-4">
        <Field
          label="Buscar segurado"
          value={busca}
          hint="Pelo menos 3 caracteres do nome ou do documento."
          onChange={(event) => setBusca(event.target.value)}
        />
        {opcoes.isError ? (
          <Alert tone="danger" title="Nao foi possivel buscar o segurado">
            {opcoes.error.message}
          </Alert>
        ) : null}
        {opcoes.isSuccess && opcoes.data.length === 0 ? (
          <p className="text-sm text-ink-600">Nenhum segurado encontrado.</p>
        ) : null}
        {(opcoes.data ?? []).length > 0 ? (
          <ul className="flex flex-col gap-2">
            {(opcoes.data ?? []).map((opcao) => (
              <li
                key={opcao.id}
                className="flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2"
              >
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-ink-900">{opcao.legal_name}</p>
                  <p className="text-2xs tabular-nums text-ink-500">
                    {formatCpfCnpj(opcao.document)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant={opcao.id === rascunho.insured_id ? 'primary' : 'secondary'}
                  onClick={() => {
                    const principal =
                      opcao.addresses.find((endereco) => endereco.is_main) ??
                      opcao.addresses[0];
                    setEscolhido(opcao);
                    alterar({
                      insured_id: opcao.id,
                      insured_label: opcao.legal_name,
                      insured_document: opcao.document,
                      insured_address_id: principal?.id ?? '',
                      insured_address_label: principal?.label ?? '',
                    });
                  }}
                >
                  {opcao.id === rascunho.insured_id ? 'Escolhido' : 'Escolher'}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        {rascunho.insured_id ? (
          <p className="text-sm text-ink-700">
            Segurado: {rascunho.insured_label} (
            {formatCpfCnpj(rascunho.insured_document)})
          </p>
        ) : null}
        <Select
          label="Endereco do segurado"
          required
          placeholder={
            rascunho.insured_id ? 'Selecione o endereco' : 'Escolha o segurado primeiro'
          }
          value={rascunho.insured_address_id}
          options={enderecos}
          error={
            fieldErrorOf(apiError, 'insured_address_id') ??
            fieldErrorOf(apiError, 'insured_id')
          }
          onChange={(event) => {
            const endereco = enderecos.find(
              (item) => item.value === event.target.value,
            );
            alterar({
              insured_address_id: event.target.value,
              insured_address_label: endereco?.label ?? '',
            });
          }}
        />
      </CardBody>
    </Card>
  );
}

/**
 * Passo "Dados do risco" (NCT-104 a NCT-130): o grupo e a modalidade, a importancia
 * segurada, a taxa (so leitura quando a empresa usa a sugerida pela faixa), a vigencia
 * e o prazo como digitados (PRC-008), a complementar e o objeto. As coberturas
 * adicionais entram depois de gerar a cotacao, como no OnPoint.
 */
export function RiscoStep({
  rascunho,
  alterar,
  apiError,
  opcoes,
  operacao,
}: PassoDoRascunhoProps & {
  opcoes: BrokerQuoteOptions;
  operacao: OperationDetail | undefined;
}) {
  // A modalidade de leiloeiro e da cotacao de pessoa fisica (P-NCT-3): o servidor a
  // recusa aqui, e a lista nao a oferece.
  const principais = opcoes.modalities.filter(
    (modalidade) => !modalidade.is_complementary && modalidade.is_auctioneer !== true,
  );
  const grupos = new Map<string, string>();
  for (const modalidade of principais) {
    if (modalidade.group) grupos.set(modalidade.group.id, modalidade.group.label);
  }
  const daModalidade = principais.filter(
    (modalidade) => !rascunho.grupo || modalidade.group?.id === rascunho.grupo,
  );
  const escolhida = opcoes.modalities.find(
    (modalidade) => modalidade.id === rascunho.modality_id,
  );
  const complementares = opcoes.modalities.filter(
    (modalidade) => modalidade.is_complementary,
  );
  const avisosDaVigencia = avisosDoPrazo(escolhida, rascunho.term_days);

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader
          title="Dados do risco"
          description="Modalidade, importancia segurada e vigencia da cotacao."
        />
        <CardBody className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Grupo da modalidade"
              value={rascunho.grupo}
              options={[
                { value: '', label: 'Todos os grupos' },
                ...[...grupos].map(([value, label]) => ({ value, label })),
              ]}
              onChange={(event) =>
                alterar({ grupo: event.target.value, modality_id: '' })
              }
            />
            <Select
              label="Modalidade"
              required
              placeholder="Selecione a modalidade"
              value={rascunho.modality_id}
              options={daModalidade.map((modalidade) => ({
                value: modalidade.id,
                label: `${modalidade.name} (${modalidade.product.label})`,
              }))}
              error={fieldErrorOf(apiError, 'modality_id')}
              onChange={(event) =>
                alterar({
                  modality_id: event.target.value,
                  complementary_modality_id: '',
                  complementary_coverage_start: '',
                })
              }
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <MoneyField
              label="Importancia Segurada (R$)"
              required
              value={rascunho.insured_amount}
              error={
                erroDaImportancia(rascunho.insured_amount) ??
                fieldErrorOf(apiError, 'insured_amount')
              }
              onValueChange={(texto) => alterar({ insured_amount: texto })}
            />
            {opcoes.rate_editable ? (
              <Field
                label="Taxa aplicada (%)"
                inputMode="decimal"
                value={rascunho.applied_rate}
                hint="Dentro da faixa vigente da modalidade."
                error={
                  rateInputError(rascunho.applied_rate) ??
                  fieldErrorOf(apiError, 'applied_rate_bps')
                }
                onChange={(event) => alterar({ applied_rate: event.target.value })}
              />
            ) : (
              <p className="self-end text-xs text-ink-600">
                A taxa e a sugerida pela faixa vigente da modalidade. Ela aparece no
                premio estimado, no topo.
              </p>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Inicio da vigencia"
              type="date"
              required
              value={rascunho.coverage_start}
              error={fieldErrorOf(apiError, 'coverage_start')}
              onChange={(event) => alterar({ coverage_start: event.target.value })}
            />
            <Field
              label="Fim da vigencia"
              type="date"
              required
              value={rascunho.coverage_end}
              error={fieldErrorOf(apiError, 'coverage_end')}
              onChange={(event) => alterar({ coverage_end: event.target.value })}
            />
            <Field
              label="Prazo (dias)"
              inputMode="numeric"
              value={rascunho.term_days}
              error={fieldErrorOf(apiError, 'term_days')}
              onChange={(event) =>
                alterar({ term_days: event.target.value.replace(/\D/g, '') })
              }
            />
          </div>
          {avisosDaVigencia.length > 0 ? (
            <Alert tone="warning" title="Prazo fora da vigencia da modalidade">
              {avisosDaVigencia.join(' ')} O aviso nao impede seguir: a trava do prazo
              esta pendente de regra (SUB-004).
            </Alert>
          ) : null}
          {escolhida?.allows_complementary && complementares.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Modalidade complementar"
                value={rascunho.complementary_modality_id}
                options={[
                  { value: '', label: 'Sem modalidade complementar' },
                  ...complementares.map((modalidade) => ({
                    value: modalidade.id,
                    label: modalidade.name,
                  })),
                ]}
                error={fieldErrorOf(apiError, 'complementary_modality_id')}
                onChange={(event) =>
                  alterar({ complementary_modality_id: event.target.value })
                }
              />
              <Field
                label="Inicio da vigencia da complementar"
                type="date"
                value={rascunho.complementary_coverage_start}
                disabled={!rascunho.complementary_modality_id}
                error={fieldErrorOf(apiError, 'complementary_coverage_start')}
                onChange={(event) =>
                  alterar({ complementary_coverage_start: event.target.value })
                }
              />
            </div>
          ) : null}
          <Textarea
            label="Objeto da garantia"
            required
            rows={3}
            maxLength={2000}
            value={rascunho.subject}
            error={fieldErrorOf(apiError, 'subject')}
            onChange={(event) => alterar({ subject: event.target.value })}
          />
          {/* NCT-215: texto opcional; obrigatoriedade e significado sem fonte. */}
          <Field
            label="Codigo do quadro de distribuicao"
            value={rascunho.distribution_code}
            maxLength={60}
            hint="Opcional. O que o codigo significa e se e obrigatorio esta a confirmar."
            error={fieldErrorOf(apiError, 'distribution_code')}
            onChange={(event) => alterar({ distribution_code: event.target.value })}
          />
        </CardBody>
      </Card>

      {operacao ? (
        <CoveragesSection
          operationId={operacao.id}
          canEditStructure={operacao.quote_actions?.can_edit === true}
          canEditTerms={operacao.quote_actions?.can_edit === true}
          canEditPremium={false}
        />
      ) : (
        <Alert tone="info" title="Coberturas adicionais">
          As coberturas adicionais entram depois de gerar a cotacao, neste mesmo passo.
        </Alert>
      )}
    </div>
  );
}
